import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import {
  CertificateStatus,
  EnrollmentStatus,
  EnrollmentSummary,
  LearningHomeResponse,
  LearningLessonSummary
} from '../../core/models/portal.models';

@Component({
  selector: 'app-learning-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './learning-page.component.html'
})
export class LearningPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  protected readonly home = signal<LearningHomeResponse | null>(null);
  protected readonly expandedEnrollmentId = signal<number | null>(null);

  ngOnInit(): void {
    this.api.learningHome().subscribe((home) => this.home.set(home));
  }

  protected statusLabel(status: EnrollmentStatus): string {
    const labels: Record<EnrollmentStatus, string> = {
      ASSIGNED: 'Zugewiesen',
      STARTED: 'Gestartet',
      IN_PROGRESS: 'In Bearbeitung',
      COMPLETED: 'Abgeschlossen',
      OVERDUE: 'Überfällig',
      FAILED: 'Nicht bestanden',
      RETRAINING_REQUIRED: 'Nachschulung erforderlich'
    };

    return labels[status] ?? status;
  }

  protected dueLabel(value: string | null): string {
    return value || 'keine Frist';
  }

  protected formatDate(value: string | null): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' }).format(new Date(value));
  }

  protected durationLabel(seconds: number): string {
    if (!seconds) {
      return 'ohne Zeitvorgabe';
    }

    return `${Math.max(Math.round(seconds / 60), 1)} Minuten`;
  }

  protected nextLesson(enrollment: EnrollmentSummary): LearningLessonSummary | null {
    return enrollment.lessons.find((lesson) => lesson.id === enrollment.nextLessonId) ?? null;
  }

  protected lessonAccessible(enrollment: EnrollmentSummary, lesson: LearningLessonSummary): boolean {
    const nextLesson = this.nextLesson(enrollment);
    return lesson.completed || nextLesson === null || lesson.lessonOrder <= nextLesson.lessonOrder;
  }

  protected enrollmentExpanded(enrollmentId: number): boolean {
    return this.expandedEnrollmentId() === enrollmentId;
  }

  protected toggleEnrollment(enrollmentId: number): void {
    this.expandedEnrollmentId.update((current) => current === enrollmentId ? null : enrollmentId);
  }

  protected certificateStatusLabel(status: CertificateStatus): string {
    const labels: Record<CertificateStatus, string> = {
      ISSUED: 'Ausgestellt',
      VALID: 'Gültig',
      EXPIRED: 'Abgelaufen',
      REVOKED: 'Widerrufen'
    };

    return labels[status] ?? status;
  }
}
